import { Paper } from "@/stores/slices/paperSlice";

/**
 * 上传来源类型
 *
 * 不再在 core 层枚举具体来源，由各插件/调用方自行约定字符串：
 * - 建议约定：`"local"` 用于 PaperUpload 主体本地/文件夹上传
 * - 其他来源（如 `"url"`、`"import"`、业务自定义插件 id 等）由插件自行指定
 */
export type UploadSource = string;

/**
 * 上传状态机
 *
 * 已在当前实现中实际使用的状态：
 * - "validating"  ：useUpload 中新增任务时的初始状态（开始做数量/格式校验）
 * - "uploading"   ：useUpload / UploadManager 在真正发起上传请求时使用
 * - "processing"  ：UploadManager 中标记"服务端处理中"（预留，视后端实现而定）
 * - "success"     ：上传/处理完成
 * - "warning"     ：重复文献（上传成功但为重复，渲染时显示为 success 样式）
 * - "error"       ：上传或处理失败
 *
 * 预留但当前未显式使用的状态（为未来更细粒度展示预留）：
 * - "idle"        ：空闲态
 * - "selecting"   ：选择文件中的态
 * - "validated"   ：校验通过、待上传
 * - "checking"    ：如查重等附加检查阶段
 * - "cancelled"   ：用户主动取消
 */
export type UploadStatus =
	| "idle"
	| "selecting"
	| "validating"
	| "validated"
	| "checking"
	| "uploading"
	| "processing"
	| "success"
	| "warning"
	| "error"
	| "cancelled";

export enum UploadErrorCode {
	FORMAT_INVALID = "FORMAT_INVALID",
	SIZE_EXCEEDED = "SIZE_EXCEEDED",
	COUNT_EXCEEDED = "COUNT_EXCEEDED",
	DUPLICATE = "DUPLICATE",
	NETWORK_ERROR = "NETWORK_ERROR",
	SERVER_ERROR = "SERVER_ERROR",
	BALANCE_INSUFFICIENT = "BALANCE_INSUFFICIENT",
	UPLOAD_LIMIT_REACHED = "UPLOAD_LIMIT_REACHED",
}

export interface UploadError {
	code: UploadErrorCode;
	message: string;
	retryable?: boolean;
}

export interface UploadItem {
	id: string;
	file: File;
	status: UploadStatus;
	progress: number;
	source: UploadSource;
	error?: UploadError;
	paper?: Paper;
	startTime?: number;
	endTime?: number;
	hash?: string;
}

export interface UploadSession {
	id: string;
	tasks: UploadItem[];
	subjectId?: string;
	createdAt: number;
	route?: string;
}

export interface UploadFormat {
	accept: string;
	extensions: string[];
	validator?: (file: File) => Promise<boolean>;
}

export interface UploadConfig {
	formats: UploadFormat[];
	maxFileSize: number;
	maxFiles: number;
	maxTotal?: number;
	concurrency: number;
}

export interface ValidationSuccess {
	valid: true;
}

export interface ValidationFailure {
	valid: false;
	error: UploadError;
}

export type ValidationResult = ValidationSuccess | ValidationFailure;

